import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import PageLayout from "../../components/common/page-layout";
import { useCrownEnv } from "../../components/crown-data-import/env/env-context";
import {
  useOutboundBenchPartnershipsQuery,
  type BenchRunReportFieldsFragment,
  type Edi214ShipmentStatus,
  type OrderStatus,
} from "../../generated/graphql";
import useIsInternalAdmin from "../api-keys-admin/hooks/use-is-internal-admin";
import { collectRunBlockers } from "./bench-report-model";
import {
  addCreatedOrders,
  buildRunInput,
  confirmProductionSendFor,
  isProductionConfirmed,
  PRODUCTION_CONFIRMATION_PHRASE,
  resolveLinkedPartnership,
  type BenchCompanyUserOption,
  type BenchDriverOption,
  type BenchOrderOption,
  type BenchOrderSelectionMode,
  type BenchPartnershipOption,
} from "./bench-run";
import BenchReportView from "./components/bench-report-view";
import BenchTargetSection from "./components/bench-target-section";
import CreateOrdersSection from "./components/create-orders-section";
import FirePanel from "./components/fire-panel";
import ScenarioOrderSection from "./components/scenario-order-section";
import RunBlockers from "./components/run-blockers";
import ScenarioPanel from "./components/scenario-panel";
import StepSelectionSection from "./components/step-selection-section";
import {
  BENCH_PARTNERSHIP_PARAM,
  tenediConnectionLinkFor,
} from "../tenedi-connections/tenedi-connection-links";

const styles = {
  body: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pt: 2,
  } satisfies SxProps<Theme>,
  centered: {
    display: "flex",
    justifyContent: "center",
    p: 6,
  } satisfies SxProps<Theme>,
  summary: {
    display: "flex",
    flexDirection: "column",
  } satisfies SxProps<Theme>,
};

const EMPTY_PARTNERSHIPS: BenchPartnershipOption[] = [];
const EMPTY_STATUSES: Edi214ShipmentStatus[] = [];

const TITLE = "Outbound test bench";

type BenchMode = "scenario" | "fire";

/**
 * Internal page that sends outbound EDI into Tenedi from the TMS's own
 * trigger points. Scenario mode walks orders through the real
 * lifecycle services; Fire mode sends one 990, 214 or 210.
 *
 * The backend decides what may send: TENEDI partnerships only, a refusal for
 * any production step that would reach someone besides Tenedi, and the
 * production opt-in. The page adds the typed confirmation in front of that
 * opt-in, and sends `confirmProductionSend` only after it.
 *
 * `?partnership=<uuid>` (the Tenedi connections page's "Test in bench")
 * preselects that partnership once the list loads.
 */
const OutboundTestBenchPage = () => {
  const { canAccess, loading } = useIsInternalAdmin();
  const { env } = useCrownEnv();
  const [mode, setMode] = useState<BenchMode>("scenario");
  const [partnership, setPartnership] = useState<BenchPartnershipOption | null>(
    null,
  );
  const [selectionMode, setSelectionMode] =
    useState<BenchOrderSelectionMode>("picked");
  const [pickedOrders, setPickedOrders] = useState<BenchOrderOption[]>([]);
  const [orderStatuses, setOrderStatuses] = useState<OrderStatus[]>([]);
  const [limit, setLimit] = useState("10");
  const [skippedStepKeys, setSkippedStepKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [companyActor, setCompanyActor] =
    useState<BenchCompanyUserOption | null>(null);
  const [testDriver, setTestDriver] = useState<BenchDriverOption | null>(null);
  const [send990ForLiveOrders, setSend990ForLiveOrders] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [dryRunReport, setDryRunReport] =
    useState<BenchRunReportFieldsFragment | null>(null);
  const [dryRunExpanded, setDryRunExpanded] = useState(true);
  const [dryRunOrderUuid, setDryRunOrderUuid] = useState<string | null>(null);
  const [runReport, setRunReport] =
    useState<BenchRunReportFieldsFragment | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [runOrderUuid, setRunOrderUuid] = useState<string | null>(null);
  const {
    data: partnershipsData,
    loading: partnershipsLoading,
    error: partnershipsError,
  } = useOutboundBenchPartnershipsQuery({
    errorPolicy: "none",
    skip: !canAccess,
  });

  const [searchParams] = useSearchParams();
  const linkedUuid = searchParams.get(BENCH_PARTNERSHIP_PARAM);
  const [linkMissing, setLinkMissing] = useState(false);

  const input = useMemo(
    () =>
      buildRunInput({
        partnership,
        mode: selectionMode,
        pickedOrders,
        orderStatuses,
        limit,
        companyActor,
        testDriver,
        skippedStepKeys,
        send990ForLiveOrders,
      }),
    [
      companyActor,
      send990ForLiveOrders,
      testDriver,
      limit,
      orderStatuses,
      partnership,
      pickedOrders,
      selectionMode,
      skippedStepKeys,
    ],
  );

  const runBlockers = useMemo(
    () => collectRunBlockers(runReport?.orders ?? []),
    [runReport],
  );

  const isProductionTarget = env === "prod";
  const sendAllowed = isProductionConfirmed(env, confirmation);
  const confirmProductionSend = confirmProductionSendFor(env, confirmation);

  const handleModeChange = useCallback((_: unknown, next: BenchMode) => {
    setMode(next);
  }, []);
  // Picked orders, the actor and the reports belong to the old partnership.
  // ScenarioPanel is keyed on the partnership, so its run stops too.
  const handlePartnershipChange = useCallback(
    (next: BenchPartnershipOption | null) => {
      setPartnership(next);
      setPickedOrders([]);
      setCompanyActor(null);
      setTestDriver(null);
      setDryRunReport(null);
      setRunReport(null);
      setRunError(null);
    },
    [],
  );
  // Applies the deep link once, when the list arrives. Choosing another
  // partnership afterwards is the operator's call, so the link must not
  // override it on a later render.
  const [linkApplied, setLinkApplied] = useState(false);
  useEffect(() => {
    if (linkApplied || partnershipsData === undefined) {
      return;
    }
    const { match, missing } = resolveLinkedPartnership(
      partnershipsData.outboundBenchPartnerships,
      linkedUuid,
    );
    if (match !== null) {
      handlePartnershipChange(match);
    }
    setLinkMissing(missing);
    setLinkApplied(true);
  }, [handlePartnershipChange, linkApplied, linkedUuid, partnershipsData]);
  const handleDryRunReport = useCallback(
    (next: BenchRunReportFieldsFragment) => {
      setDryRunReport(next);
      setDryRunExpanded(true);
    },
    [],
  );
  const handleDryRunToggle = useCallback((_: unknown, expanded: boolean) => {
    setDryRunExpanded(expanded);
  }, []);
  const handleOrdersCreated = useCallback((created: BenchOrderOption[]) => {
    setSelectionMode("picked");
    setPickedOrders((current) => addCreatedOrders(current, created));
  }, []);
  const handleConfirmationChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setConfirmation(event.target.value);
    },
    [],
  );

  if (loading) {
    return (
      <PageLayout title={TITLE}>
        <Box sx={styles.centered}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!canAccess) {
    return (
      <PageLayout title={TITLE}>
        <Box sx={styles.body}>
          <Alert severity="error">
            You do not have access to internal tools.
          </Alert>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      subtitle="Send outbound 990, 214 and 210 into Tenedi through the real TMS services."
      title={TITLE}
    >
      <Box sx={styles.body}>
        <Alert severity="info">
          Only TENEDI partnerships can run. The bench refuses an order if any
          partnership on its customer routes elsewhere. Every run suppresses the
          customer&apos;s emails, texts and pushes; the plan lists them as
          suppressed, and outside production it lists the webhooks each step
          would send.
        </Alert>
        {isProductionTarget && (
          <Alert severity="error">
            <AlertTitle>Production</AlertTitle>
            The target environment is production. The bench runs only orders it
            created (HAWB BENCH-&lt;stamp&gt;-&lt;n&gt;) and refuses any step
            that would reach anyone besides Tenedi. Type{" "}
            <strong>{PRODUCTION_CONFIRMATION_PHRASE}</strong> to allow sends
            from this page.
            <TextField
              fullWidth
              label={`Type ${PRODUCTION_CONFIRMATION_PHRASE} to confirm`}
              size="small"
              value={confirmation}
              onChange={handleConfirmationChange}
            />
          </Alert>
        )}
        {linkMissing && (
          <Alert severity="warning">
            Partnership not found or not sent to Tenedi. Pick one below, or fix
            it on the Tenedi connections page.
          </Alert>
        )}
        {partnership !== null && (
          <Box>
            <Button
              component={RouterLink}
              size="small"
              to={tenediConnectionLinkFor({
                companyUuid: partnership.companyUuid,
                organizationUuid: partnership.contactUuid,
              })}
            >
              Set up this connection
            </Button>
          </Box>
        )}
        {partnershipsError !== undefined && (
          <Alert severity="error">
            Could not load the partnerships: {partnershipsError.message}
          </Alert>
        )}
        <BenchTargetSection
          companyActor={companyActor}
          partnership={partnership}
          partnerships={
            partnershipsData?.outboundBenchPartnerships ?? EMPTY_PARTNERSHIPS
          }
          partnershipsLoading={partnershipsLoading}
          testDriver={testDriver}
          onCompanyActorChange={setCompanyActor}
          onPartnershipChange={handlePartnershipChange}
          onTestDriverChange={setTestDriver}
        />
        <Tabs value={mode} onChange={handleModeChange}>
          <Tab label="Scenario" value="scenario" />
          <Tab label="Fire" value="fire" />
        </Tabs>
        {mode === "scenario" ? (
          <>
            <CreateOrdersSection
              companyName={partnership?.companyName ?? null}
              confirmProductionSend={confirmProductionSend}
              contactName={partnership?.contactName ?? null}
              partnershipUuid={partnership?.uuid ?? null}
              production={isProductionTarget}
              onCreated={handleOrdersCreated}
            />
            <ScenarioOrderSection
              limit={limit}
              mode={selectionMode}
              orderStatuses={orderStatuses}
              partnershipUuid={partnership?.uuid ?? null}
              pickedOrders={pickedOrders}
              onLimitChange={setLimit}
              onModeChange={setSelectionMode}
              onOrderStatusesChange={setOrderStatuses}
              onPickedOrdersChange={setPickedOrders}
            />
            <StepSelectionSection
              send990ForLiveOrders={send990ForLiveOrders}
              skippedKeys={skippedStepKeys}
              onChange={setSkippedStepKeys}
              onSend990ForLiveOrdersChange={setSend990ForLiveOrders}
            />
            <ScenarioPanel
              key={partnership?.uuid ?? "none"}
              confirmProductionSend={confirmProductionSend}
              disabled={!sendAllowed}
              input={input}
              onDryRunReport={handleDryRunReport}
              onRunError={setRunError}
              onRunReport={setRunReport}
            />
          </>
        ) : (
          <FirePanel
            key={partnership?.uuid ?? "none"}
            confirmProductionSend={confirmProductionSend}
            defaultOrder={pickedOrders[0] ?? null}
            disabled={!sendAllowed}
            partnershipUuid={partnership?.uuid ?? null}
            statuses={partnership?.edi214ShipmentStatuses ?? EMPTY_STATUSES}
          />
        )}
        {mode === "scenario" && dryRunReport !== null && (
          <Accordion expanded={dryRunExpanded} onChange={handleDryRunToggle}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={styles.summary}>
                <Typography variant="subtitle1">
                  Dry run: plan and pre-flight
                </Typography>
                <Typography variant="body2">
                  Environment {dryRunReport.environment}
                  {dryRunReport.production ? " (production)" : ""} ·{" "}
                  {dryRunReport.orders.length} order(s). Nothing was sent.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <BenchReportView
                orders={dryRunReport.orders}
                selectedOrderUuid={dryRunOrderUuid}
                variant="plan"
                onSelectOrder={setDryRunOrderUuid}
              />
            </AccordionDetails>
          </Accordion>
        )}
        {mode === "scenario" && (runReport !== null || runError !== null) && (
          <Stack spacing={2}>
            <Typography variant="h6">Run results</Typography>
            <RunBlockers
              blockers={runBlockers}
              runError={runError}
              onOpenOrder={setRunOrderUuid}
            />
            {runReport !== null && (
              <>
                <Alert severity={runReport.production ? "warning" : "info"}>
                  Environment {runReport.environment}
                  {runReport.production ? " (production)" : ""} ·{" "}
                  {runReport.orders.length} order(s)
                </Alert>
                <BenchReportView
                  orders={runReport.orders}
                  selectedOrderUuid={runOrderUuid}
                  variant="run"
                  onSelectOrder={setRunOrderUuid}
                />
              </>
            )}
          </Stack>
        )}
      </Box>
    </PageLayout>
  );
};

export default OutboundTestBenchPage;
